import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, ShoppingCart, ChevronDown, NirCoinIcon, Store, Users } from "../components/common/Icons";
import CardBinder from "../components/common/CardBinder";
import CartasStorePage from "./CartasStorePage";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Card icon for subsection
const CardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <path d="M12 8v8"/>
    <path d="M8 12h8"/>
  </svg>
);

// Box icon for products
const BoxIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

// Get rarity badge style
const getRarityBadgeStyle = (rarity) => {
  const styles = {
    'Secret Rare': 'bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-500 text-black',
    'Ultra Rare': 'bg-gradient-to-r from-purple-400 to-pink-500 text-white',
    'Super Rare': 'bg-gradient-to-r from-blue-400 to-cyan-500 text-white',
    'Rare': 'bg-gradient-to-r from-green-400 to-emerald-500 text-white',
    'Common': 'bg-gray-600 text-white'
  };
  return styles[rarity] || styles['Common'];
};

// Card Detail Modal - Moved outside component
const CardDetailModal = ({ card, onClose, onAddToCart, payWithNirCoin }) => {
  if (!card) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
      onClick={onClose}
      data-testid="card-detail-modal"
    >
      <div 
        className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-4 sm:p-6 max-w-2xl w-full border border-gray-700 shadow-2xl max-h-[90vh] overflow-y-auto" 
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors z-10"
          data-testid="modal-close"
        >
          ✕
        </button>

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          {/* Card Image */}
          <div className="w-full sm:w-48 md:w-56 flex-shrink-0 mx-auto sm:mx-0">
            <div className="aspect-[63/88] bg-gray-800 rounded-lg overflow-hidden shadow-xl ring-2 ring-gray-700">
              <img 
                src={card.imageFull || card.image} 
                alt={card.name} 
                className="w-full h-full object-cover" 
                onError={(e) => { 
                  e.target.onerror = null;
                  e.target.src = `https://placehold.co/300x420/1a1a2e/ffffff?text=${encodeURIComponent(card.name?.substring(0, 15) || 'Card')}`; 
                }} 
              />
            </div>
          </div>

          {/* Card Info */}
          <div className="flex-1 flex flex-col">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2 leading-tight">{card.name}</h3>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {card.rarity && (
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getRarityBadgeStyle(card.rarity)}`}>
                  {card.rarity}
                </span>
              )}
              {card.type && (
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-purple-600 text-white capitalize">
                  {card.type}
                </span>
              )}
            </div>

            {/* Card Stats for monsters */}
            {(card.atk !== undefined || card.def !== undefined || card.level) && (
              <div className="flex flex-wrap gap-3 mb-3 text-sm">
                {card.level && (
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-400">★</span>
                    <span className="text-gray-300">Nivel {card.level}</span>
                  </div>
                )}
                {card.atk !== undefined && (
                  <div className="flex items-center gap-1">
                    <span className="text-red-400 font-bold">ATK</span>
                    <span className="text-gray-300">{card.atk}</span>
                  </div>
                )}
                {card.def !== undefined && (
                  <div className="flex items-center gap-1">
                    <span className="text-blue-400 font-bold">DEF</span>
                    <span className="text-gray-300">{card.def}</span>
                  </div>
                )}
              </div>
            )}

            {/* Attribute and Race */}
            {(card.attribute || card.race) && (
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                {card.attribute && (
                  <span className="px-2 py-1 bg-gray-800 rounded text-gray-400">
                    {card.attribute}
                  </span>
                )}
                {card.race && (
                  <span className="px-2 py-1 bg-gray-800 rounded text-gray-400">
                    {card.race}
                  </span>
                )}
              </div>
            )}

            {/* Description */}
            {card.description && (
              <div className="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                <p className="text-xs text-gray-400 leading-relaxed max-h-24 overflow-y-auto">
                  {card.description}
                </p>
              </div>
            )}

            <div className="space-y-2 mb-4">
              <div className="flex items-baseline gap-2">
                <span className="text-gray-400 text-sm">Precio:</span>
                <span className="text-3xl font-black bg-gradient-to-r from-pink-400 to-red-400 bg-clip-text text-transparent">
                  ${card.price}
                </span>
              </div>
              
              {card.stock !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-sm">Stock:</span>
                  <span className={`font-bold ${card.stock > 3 ? 'text-green-400' : card.stock > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {card.stock > 0 ? `${card.stock} disponibles` : 'Agotado'}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-auto space-y-2">
              <button
                onClick={() => { onAddToCart(card); onClose(); }}
                disabled={card.stock === 0}
                className="w-full py-3 bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 rounded-xl font-bold text-white flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-pink-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="modal-add-cart"
              >
                <ShoppingCart size={20} />
                Agregar al Carrito
              </button>
              
              {payWithNirCoin && (
                <p className="text-center text-xs text-gray-400">
                  o paga con <span className="text-yellow-400 font-bold">{Math.round(parseFloat(card.price) * 2)} NirCoins</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketplacePage = ({ onAddToCart, isLoggedIn, nirtcgBalance }) => {
  const [selectedGame, setSelectedGame] = useState('yugioh');
  const [selectedSection, setSelectedSection] = useState('public');
  const [storeSubSection, setStoreSubSection] = useState('cards'); // 'cards' or 'products'
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [products, setProducts] = useState([]);
  const [storeCards, setStoreCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payWithNirCoin, setPayWithNirCoin] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [binderColor, setBinderColor] = useState('cyan');
  const [cardTypeFilter, setCardTypeFilter] = useState('all'); // 'all', 'monster', 'spell', 'trap'
  const [filters, setFilters] = useState({ 
    category: [], 
    priceMin: 0,
    priceMax: 500, 
    availability: ['inStock']
  });

  // Card type filters
  const cardTypes = [
    { id: 'all', name: 'Todas', icon: '🃏', color: 'from-gray-500 to-gray-600' },
    { id: 'monster', name: 'Monstruos', icon: '👹', color: 'from-orange-500 to-red-500' },
    { id: 'spell', name: 'Magias', icon: '✨', color: 'from-green-500 to-emerald-500' },
    { id: 'trap', name: 'Trampas', icon: '⚠️', color: 'from-purple-500 to-pink-500' },
  ];

  const games = [
    { id: 'yugioh', name: 'Yu-Gi-Oh!' },
    { id: 'pokemon', name: 'Pokémon' },
    { id: 'digimon', name: 'Digimon' },
    { id: 'magic', name: 'Magic' }
  ];

  const sections = [
    { id: 'public', name: 'Market Público', icon: <Users size={18} />, description: 'Productos de la comunidad' },
    { id: 'store', name: 'Market en Tienda', icon: <Store />, description: 'Exclusivos de Nirvana TCG' },
    { id: 'cartas', name: 'Cartas', icon: <CardIcon />, description: 'Tienda de cartas sueltas' },
  ];

  const storeSubSections = [
    { id: 'cards', name: 'Cartas en Tienda', icon: <CardIcon />, description: 'Carpeta de cartas disponibles' },
    { id: 'products', name: 'Productos en Tienda', icon: <BoxIcon />, description: 'Accesorios y sellados' },
  ];

  const categories = [
    { id: 'cartas', name: 'Cartas Sueltas' },
    { id: 'sellado', name: 'Producto Sellado' },
    { id: 'accesorios', name: 'Accesorios' },
    { id: 'mazos', name: 'Mazos' }
  ];

  const binderColors = [
    { id: 'cyan', name: 'Turquesa', color: 'bg-cyan-500' },
    { id: 'purple', name: 'Morado', color: 'bg-purple-500' },
    { id: 'red', name: 'Rojo', color: 'bg-red-500' },
    { id: 'blue', name: 'Azul', color: 'bg-blue-500' },
    { id: 'black', name: 'Negro', color: 'bg-gray-800' },
    { id: 'gold', name: 'Dorado', color: 'bg-gradient-to-r from-yellow-400 to-amber-500' },
  ];

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('game', selectedGame);
      params.append('section', selectedSection);
      params.append('limit', '50');
      
      const res = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(res.data.products || []);
    } catch (e) {
      console.error('Error loading products:', e);
      setProducts(getSampleProducts());
    }
    setLoading(false);
  };

  const loadStoreCards = async () => {
    try {
      // Load cards from the real database
      const res = await axios.get(`${API}/cards?limit=200`);
      const rawCards = res.data.cards || [];
      
      // Map database cards to CardBinder format
      const mappedCards = rawCards.map((card, idx) => {
        // Determine card type based on frameType or type field
        let cardType = 'monster';
        const frameType = card.frameType?.toLowerCase() || '';
        const type = card.type?.toLowerCase() || '';
        
        if (frameType.includes('spell') || type.includes('spell')) {
          cardType = 'spell';
        } else if (frameType.includes('trap') || type.includes('trap')) {
          cardType = 'trap';
        }
        
        // Determine rarity from card sets if available
        let rarity = 'Common';
        if (card.card_sets && card.card_sets.length > 0) {
          const setRarity = card.card_sets[0].set_rarity;
          if (setRarity) {
            if (setRarity.includes('Secret')) rarity = 'Secret Rare';
            else if (setRarity.includes('Ultra')) rarity = 'Ultra Rare';
            else if (setRarity.includes('Super')) rarity = 'Super Rare';
            else if (setRarity.includes('Rare')) rarity = 'Rare';
          }
        }
        
        // Get price from card_prices if available
        let price = Math.random() * 50 + 5; // Default random price as number
        if (card.card_prices && card.card_prices.length > 0) {
          const tcgPrice = parseFloat(card.card_prices[0].tcgplayer_price || 0);
          const cardmarketPrice = parseFloat(card.card_prices[0].cardmarket_price || 0);
          if (tcgPrice > 0) {
            price = tcgPrice;
          } else if (cardmarketPrice > 0) {
            price = cardmarketPrice;
          }
        }
        const priceStr = price.toFixed(2);
        
        // Get image URL
        let imageUrl = `https://images.ygoprodeck.com/images/cards_small/${card.id}.jpg`;
        let imageFull = `https://images.ygoprodeck.com/images/cards/${card.id}.jpg`;
        if (card.card_images && card.card_images.length > 0) {
          imageUrl = card.card_images[0].image_url_small || imageUrl;
          imageFull = card.card_images[0].image_url || imageFull;
        }
        
        return {
          id: `card-${card.id || idx}`,
          dbId: card.id,
          name: card.name || 'Unknown Card',
          image: imageUrl,
          imageFull: imageFull,
          price: priceStr,
          rarity: rarity,
          stock: Math.floor(Math.random() * 10) + 1, // Random stock for demo
          type: cardType,
          description: card.desc || '',
          atk: card.atk,
          def: card.def,
          level: card.level,
          attribute: card.attribute,
          race: card.race
        };
      });
      
      setStoreCards(mappedCards);
    } catch (e) {
      console.error('Error loading store cards:', e);
      // Fallback to sample cards if API fails
      setStoreCards(getSampleStoreCards());
    }
  };

  useEffect(() => {
    loadProducts();
    if (selectedSection === 'store' && storeSubSection === 'cards') {
      loadStoreCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame, selectedSection, storeSubSection]);

  const getSampleProducts = () => [
    { id: '1', name: 'Booster Box: Era de la Dominación', price: 89.99, price_nircoin: 180, image: 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400', category: 'sellado', game: 'yugioh', section: selectedSection, in_stock: true },
    { id: '2', name: 'Structure Deck: Dragón del Abismo', price: 24.99, price_nircoin: 50, image: 'https://images.unsplash.com/photo-1605870445919-838d190e8e1b?w=400', category: 'sellado', game: 'yugioh', section: selectedSection, in_stock: true },
    { id: '3', name: 'Protectores Premium Dragon Shield', price: 12.99, price_nircoin: 26, image: 'https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=400', category: 'accesorios', game: 'yugioh', section: selectedSection, in_stock: true },
    { id: '4', name: 'Tapete de Juego Oficial', price: 34.99, price_nircoin: 70, image: 'https://images.unsplash.com/photo-1612404730960-5c71577fca11?w=400', category: 'accesorios', game: 'yugioh', section: selectedSection, in_stock: true },
  ];

  const getSampleStoreCards = () => {
    // Extensive Yu-Gi-Oh! card database for multiple pages (4x4x2 = 32 per spread)
    // Types: monster, spell, trap
    const cardDatabase = [
      // Page 1 - Classic Monsters
      { name: 'Dragón Blanco de Ojos Azules', id: 89631139, price: 45.99, rarity: 'Ultra Rare', stock: 3, type: 'monster' },
      { name: 'Mago Oscuro', id: 46986414, price: 38.50, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Dragón Negro de Ojos Rojos', id: 74677422, price: 25.00, rarity: 'Super Rare', stock: 5, type: 'monster' },
      { name: 'Exodia el Prohibido', id: 33396948, price: 120.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Brazo Derecho del Prohibido', id: 70903634, price: 15.00, rarity: 'Rare', stock: 4, type: 'monster' },
      { name: 'Brazo Izquierdo del Prohibido', id: 7902349, price: 15.00, rarity: 'Rare', stock: 4, type: 'monster' },
      { name: 'Pierna Derecha del Prohibido', id: 8124921, price: 15.00, rarity: 'Rare', stock: 3, type: 'monster' },
      { name: 'Pierna Izquierda del Prohibido', id: 44519536, price: 15.00, rarity: 'Rare', stock: 3, type: 'monster' },
      { name: 'Chica Maga Oscura', id: 38033121, price: 55.00, rarity: 'Secret Rare', stock: 2, type: 'monster' },
      { name: 'Kuriboh', id: 40640057, price: 8.50, rarity: 'Rare', stock: 8, type: 'monster' },
      { name: 'Jinzo', id: 77585513, price: 12.00, rarity: 'Super Rare', stock: 4, type: 'monster' },
      { name: 'Ciberdragón', id: 70095154, price: 18.00, rarity: 'Super Rare', stock: 6, type: 'monster' },
      { name: 'Dragón de Polvo de Estrellas', id: 44508094, price: 35.00, rarity: 'Ultra Rare', stock: 3, type: 'monster' },
      { name: 'Dragón Rosa Negra', id: 73580471, price: 28.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Dragón Archidemoníaco Rojo', id: 35112613, price: 22.00, rarity: 'Super Rare', stock: 4, type: 'monster' },
      { name: 'Número 39: Utopía', id: 84013237, price: 15.00, rarity: 'Super Rare', stock: 5, type: 'monster' },
      
      // Page 2 - Egyptian Gods & Legendary
      { name: 'Slifer el Dragón del Cielo', id: 10000000, price: 85.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Obelisco el Atormentador', id: 10000001, price: 85.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Dragón Alado de Ra', id: 10000002, price: 95.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Dragón Definitivo de Ojos Azules', id: 23995346, price: 150.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Dragón de 5 Cabezas', id: 99267150, price: 42.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Mago del Caos Negro', id: 88622073, price: 65.00, rarity: 'Secret Rare', stock: 2, type: 'monster' },
      { name: 'Gaia el Campeón de la Fuerza', id: 6368038, price: 10.00, rarity: 'Rare', stock: 6, type: 'monster' },
      { name: 'Soldado del Brillo Negro', id: 5405694, price: 75.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Dragón Maestro de Ojos Azules', id: 53183600, price: 180.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Héroe Elemental Neos', id: 89943723, price: 20.00, rarity: 'Super Rare', stock: 4, type: 'monster' },
      { name: 'Yubel', id: 78371393, price: 35.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Dragón Arcoíris', id: 79856792, price: 45.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Mago del Tiempo', id: 75375465, price: 12.00, rarity: 'Rare', stock: 5, type: 'monster' },
      { name: 'Invocador del Cráneo', id: 70781052, price: 8.00, rarity: 'Common', stock: 10, type: 'monster' },
      { name: 'Rey Calavera', id: 36021814, price: 5.00, rarity: 'Common', stock: 12, type: 'monster' },
      { name: 'Monstruo Renacido', id: 83764718, price: 25.00, rarity: 'Super Rare', stock: 3, type: 'spell' },
      
      // Page 3 - Synchro & XYZ Era
      { name: 'Dragón de la Barrera de Hielo Trishula', id: 52687916, price: 45.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Dragón Quasar de Disparo', id: 94677145, price: 55.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Número 101: Arca del Silencio Honor', id: 48739166, price: 18.00, rarity: 'Super Rare', stock: 4, type: 'monster' },
      { name: 'Castel, el Espadachín Avemística', id: 82633039, price: 12.00, rarity: 'Super Rare', stock: 5, type: 'monster' },
      { name: 'Dante, Viajero del Abismo Ardiente', id: 18144506, price: 28.00, rarity: 'Ultra Rare', stock: 3, type: 'monster' },
      { name: 'Virgil, Estrella Rock del Abismo', id: 85289965, price: 15.00, rarity: 'Super Rare', stock: 4, type: 'monster' },
      { name: 'Dragón de Cristal Ala Clara', id: 50954680, price: 35.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Dragón Cósmico Blazar', id: 21844576, price: 48.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Utopía el Relámpago', id: 56832966, price: 30.00, rarity: 'Ultra Rare', stock: 3, type: 'monster' },
      { name: 'Número C39: Utopía Ray V', id: 21521304, price: 22.00, rarity: 'Super Rare', stock: 4, type: 'monster' },
      { name: 'Dragón de Péndulo de Ojos Anómalos', id: 16178681, price: 40.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Nirvana Alto Paladín', id: 80896940, price: 35.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Scarlight Dragón Archidemoníaco Rojo', id: 80666118, price: 25.00, rarity: 'Super Rare', stock: 3, type: 'monster' },
      { name: 'Crystal Wing Synchro Dragon', id: 50954680, price: 42.00, rarity: 'Secret Rare', stock: 2, type: 'monster' },
      { name: 'Ignister Prominence', id: 18239909, price: 18.00, rarity: 'Super Rare', stock: 4, type: 'monster' },
      { name: 'Odd-Eyes Vortex Dragon', id: 35075702, price: 20.00, rarity: 'Super Rare', stock: 3, type: 'monster' },
      
      // Page 4 - Link Era & Modern
      { name: 'Firewall Dragon', id: 5043010, price: 35.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Borrelsword Dragon', id: 85289965, price: 55.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Borreload Dragon', id: 31833038, price: 45.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Accesscode Talker', id: 86066372, price: 68.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Apollousa, Arco de la Diosa', id: 4280258, price: 42.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Knightmare Unicorn', id: 38342335, price: 18.00, rarity: 'Super Rare', stock: 5, type: 'monster' },
      { name: 'Knightmare Phoenix', id: 2857636, price: 12.00, rarity: 'Super Rare', stock: 6, type: 'monster' },
      { name: 'I:P Masquerena', id: 65741786, price: 35.00, rarity: 'Ultra Rare', stock: 3, type: 'monster' },
      { name: 'Selene, Reina de las Maestras Magas', id: 45819647, price: 28.00, rarity: 'Ultra Rare', stock: 3, type: 'monster' },
      { name: 'Dragón Albión el Santuario', id: 38811586, price: 75.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Mirrorjade the Iceblade Dragon', id: 44146295, price: 85.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Branded Despia Dramaturgia', id: 82738008, price: 55.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Tearlaments Kitkallos', id: 90381314, price: 45.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Spright Elf', id: 22617205, price: 38.00, rarity: 'Ultra Rare', stock: 2, type: 'monster' },
      { name: 'Zeus Divino, Rey del Olimpo', id: 90448279, price: 65.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      { name: 'Baronne de Fleur', id: 84815190, price: 72.00, rarity: 'Secret Rare', stock: 1, type: 'monster' },
      
      // Page 5 - Spell Cards
      { name: 'Agujero Negro', id: 53129443, price: 5.00, rarity: 'Common', stock: 15, type: 'spell' },
      { name: 'Tormenta de Plumas Arpía', id: 18144506, price: 8.00, rarity: 'Rare', stock: 10, type: 'spell' },
      { name: 'Llamada del Cazador', id: 68765166, price: 12.00, rarity: 'Super Rare', stock: 6, type: 'spell' },
      { name: 'Fusión Milagrosa', id: 45906428, price: 15.00, rarity: 'Super Rare', stock: 5, type: 'spell' },
      { name: 'Pot de Codicia', id: 55144522, price: 35.00, rarity: 'Ultra Rare', stock: 2, type: 'spell' },
      { name: 'Graceful Charity', id: 79571449, price: 40.00, rarity: 'Ultra Rare', stock: 2, type: 'spell' },
      { name: 'Change of Heart', id: 4031928, price: 45.00, rarity: 'Secret Rare', stock: 1, type: 'spell' },
      { name: 'Raigeki', id: 12580477, price: 28.00, rarity: 'Ultra Rare', stock: 3, type: 'spell' },
      { name: 'Prohibido Lance', id: 27243130, price: 8.00, rarity: 'Rare', stock: 8, type: 'spell' },
      { name: 'Twinsters', id: 43898403, price: 12.00, rarity: 'Super Rare', stock: 6, type: 'spell' },
      { name: 'Triple Tactics Talent', id: 25311006, price: 45.00, rarity: 'Secret Rare', stock: 2, type: 'spell' },
      { name: 'Lightning Storm', id: 14532163, price: 52.00, rarity: 'Secret Rare', stock: 1, type: 'spell' },
      { name: 'Forbidden Droplet', id: 24299458, price: 75.00, rarity: 'Secret Rare', stock: 1, type: 'spell' },
      { name: 'Dark Ruler No More', id: 54693926, price: 18.00, rarity: 'Super Rare', stock: 5, type: 'spell' },
      { name: 'Super Polymerization', id: 48130397, price: 15.00, rarity: 'Super Rare', stock: 5, type: 'spell' },
      { name: 'Branded Fusion', id: 44362883, price: 35.00, rarity: 'Ultra Rare', stock: 3, type: 'spell' },
      
      // Page 6 - Trap Cards & Hand Traps
      { name: 'Solemn Judgment', id: 41420027, price: 22.00, rarity: 'Ultra Rare', stock: 3, type: 'trap' },
      { name: 'Solemn Warning', id: 84749824, price: 15.00, rarity: 'Super Rare', stock: 5, type: 'trap' },
      { name: 'Infinite Impermanence', id: 10045474, price: 35.00, rarity: 'Secret Rare', stock: 2, type: 'trap' },
      { name: 'Called by the Grave', id: 24224830, price: 18.00, rarity: 'Super Rare', stock: 4, type: 'spell' },
      { name: 'Evenly Matched', id: 15693423, price: 35.00, rarity: 'Secret Rare', stock: 2, type: 'trap' },
      { name: 'Red Reboot', id: 32566831, price: 10.00, rarity: 'Rare', stock: 8, type: 'trap' },
      { name: 'Torrential Tribute', id: 53582587, price: 8.00, rarity: 'Rare', stock: 10, type: 'trap' },
      { name: 'Mirror Force', id: 44095762, price: 10.00, rarity: 'Super Rare', stock: 8, type: 'trap' },
      { name: 'Bottomless Trap Hole', id: 29401950, price: 6.00, rarity: 'Rare', stock: 12, type: 'trap' },
      { name: 'Compulsory Evacuation Device', id: 94192409, price: 5.00, rarity: 'Common', stock: 15, type: 'trap' },
      { name: 'Skill Drain', id: 82732705, price: 25.00, rarity: 'Ultra Rare', stock: 3, type: 'trap' },
      { name: 'Imperial Order', id: 61740673, price: 30.00, rarity: 'Ultra Rare', stock: 2, type: 'trap' },
      { name: 'Ash Blossom & Joyous Spring', id: 14558127, price: 48.00, rarity: 'Secret Rare', stock: 2, type: 'monster' },
      { name: 'Effect Veiler', id: 97268402, price: 10.00, rarity: 'Super Rare', stock: 8, type: 'monster' },
      { name: 'Maxx "C"', id: 23434538, price: 55.00, rarity: 'Secret Rare', stock: 2, type: 'monster' },
      { name: 'Nibiru, el Ser Primordial', id: 27204311, price: 28.00, rarity: 'Ultra Rare', stock: 3, type: 'monster' }
    ];
    
    // Generate cards with proper image URLs from YGOProdeck API
    return cardDatabase.map((card, idx) => ({
      id: `card-${idx}`,
      name: card.name,
      image: `https://images.ygoprodeck.com/images/cards_small/${card.id}.jpg`,
      imageFull: `https://images.ygoprodeck.com/images/cards/${card.id}.jpg`,
      price: card.price.toFixed(2),
      rarity: card.rarity,
      stock: card.stock,
      type: card.type
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFilters(prev => ({
      ...prev,
      category: prev.category.includes(categoryId)
        ? prev.category.filter(c => c !== categoryId)
        : [...prev.category, categoryId]
    }));
  };

  const handleAvailabilityToggle = (type) => {
    setFilters(prev => ({
      ...prev,
      availability: prev.availability.includes(type)
        ? prev.availability.filter(a => a !== type)
        : [...prev.availability, type]
    }));
  };

  const filteredProducts = products.filter(p => {
    if (filters.category.length > 0 && !filters.category.includes(p.category)) return false;
    if (p.price < filters.priceMin || p.price > filters.priceMax) return false;
    
    if (filters.availability.length > 0) {
      const matchesAvailability = filters.availability.some(av => {
        if (av === 'inStock') return p.in_stock && !p.is_preorder;
        if (av === 'preorder') return p.is_preorder;
        return false;
      });
      if (!matchesAvailability && filters.availability.length > 0) return false;
    }
    
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const clearFilters = () => {
    setFilters({ category: [], priceMin: 0, priceMax: 500, availability: [] });
  };

  const handleAddToCart = (product) => {
    const item = {
      ...product,
      useNirCoin: payWithNirCoin
    };
    onAddToCart(item);
  };

  const handleCardClick = (card) => {
    console.log('Card clicked:', card);
    setSelectedCard(card);
  };

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="marketplace-page">
      <div className="container mx-auto px-4 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors text-sm">
          <ArrowLeft /> Volver al Inicio
        </Link>
      </div>

      {/* Header */}
      <div className="border-b-2 border-pink-500/30 pb-6 md:pb-8">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-4xl font-black mb-3 md:mb-4">
            <span className="bg-gradient-to-r from-red-500 via-pink-500 to-yellow-400 bg-clip-text text-transparent">Marketplace</span>
          </h1>
          <p className="text-gray-400 mb-4 md:mb-6 text-sm md:text-base">Encuentra todo lo que necesitas para tus duelos y colecciones.</p>
          
          {/* Section Selection */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
            {sections.map(sec => (
              <button
                key={sec.id}
                onClick={() => setSelectedSection(sec.id)}
                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  selectedSection === sec.id 
                    ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg shadow-pink-500/30' 
                    : 'border-2 border-purple-600 text-gray-400 hover:border-yellow-400 hover:text-yellow-400'
                }`}
                data-testid={`section-btn-${sec.id}`}
              >
                {sec.icon}
                <div className="text-left">
                  <p className="font-bold">{sec.name}</p>
                  <p className="text-xs opacity-75">{sec.description}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Store Sub-sections (only when "Market en Tienda" is selected) */}
          {selectedSection === 'store' && (
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {storeSubSections.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setStoreSubSection(sub.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                    storeSubSection === sub.id 
                      ? 'bg-yellow-400 text-black' 
                      : 'bg-purple-900/50 text-gray-400 hover:text-white hover:bg-purple-800/50'
                  }`}
                  data-testid={`store-sub-${sub.id}`}
                >
                  {sub.icon}
                  <span>{sub.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Game Selection (hide for cards sub-section as it shows all, and for cartas section) */}
          {!(selectedSection === 'store' && storeSubSection === 'cards') && selectedSection !== 'cartas' && (
            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-4 md:mb-6">
              {games.map(game => (
                <button
                  key={game.id}
                  onClick={() => setSelectedGame(game.id)}
                  className={`px-4 md:px-6 py-1.5 md:py-2 rounded-full font-semibold text-sm md:text-base transition-all ${
                    selectedGame === game.id 
                      ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white' 
                      : 'border border-gray-600 text-gray-400 hover:border-yellow-400 hover:text-yellow-400'
                  }`}
                  data-testid={`game-btn-${game.id}`}
                >
                  {game.name}
                </button>
              ))}
            </div>
          )}

          {/* Search (hide for card binder view and cartas section) */}
          {!(selectedSection === 'store' && storeSubSection === 'cards') && selectedSection !== 'cartas' && (
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-2.5 md:py-3 bg-purple-900/30 border border-purple-700 rounded-full text-white text-sm md:text-base placeholder:text-gray-500 focus:outline-none focus:border-pink-500"
                data-testid="search-input"
              />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Cartas Store View */}
        {selectedSection === 'cartas' ? (
          <CartasStorePage onAddToCart={handleAddToCart} />
        ) : selectedSection === 'store' && storeSubSection === 'cards' ? (
          <div>
            {/* Binder Color Selection */}
            <div className="flex flex-wrap justify-center items-center gap-3 mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-800">
              <span className="text-gray-400 text-sm font-medium">Color de carpeta:</span>
              <div className="flex gap-2">
                {binderColors.map(color => (
                  <button
                    key={color.id}
                    onClick={() => setBinderColor(color.id)}
                    className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                      binderColor === color.id 
                        ? 'border-yellow-400 scale-110 ring-2 ring-yellow-400/30' 
                        : 'border-gray-600 hover:border-gray-400'
                    } ${color.color}`}
                    title={color.name}
                    data-testid={`binder-color-${color.id}`}
                  />
                ))}
              </div>
            </div>

            {/* Info text */}
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Carpeta de Cartas
                </span>
              </h2>
              <p className="text-gray-400 text-sm max-w-md mx-auto">
                Explora nuestra colección de cartas. Haz clic en cualquier carta para ver detalles y agregarla al carrito.
              </p>
            </div>

            {/* Card Type Filters */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {cardTypes.map(ct => (
                <button
                  key={ct.id}
                  onClick={() => setCardTypeFilter(ct.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                    cardTypeFilter === ct.id
                      ? `bg-gradient-to-r ${ct.color} text-white shadow-lg`
                      : 'bg-gray-800/80 text-gray-400 border border-gray-700 hover:border-gray-500'
                  }`}
                  data-testid={`card-type-${ct.id}`}
                >
                  <span>{ct.icon}</span>
                  <span>{ct.name}</span>
                  <span className="text-xs opacity-75">
                    ({ct.id === 'all' 
                      ? storeCards.length 
                      : storeCards.filter(c => c.type === ct.id).length})
                  </span>
                </button>
              ))}
            </div>

            {/* Navigation hint */}
            <div className="flex justify-center gap-4 mb-6 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-800 rounded border border-gray-700">←</kbd>
                <kbd className="px-1.5 py-0.5 bg-gray-800 rounded border border-gray-700">→</kbd>
                <span>Navegar</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-1 bg-gray-600 rounded"></span>
                <span>Deslizar</span>
              </span>
            </div>

            {/* Card Binder Component */}
            <CardBinder 
              cards={cardTypeFilter === 'all' ? storeCards : storeCards.filter(c => c.type === cardTypeFilter)} 
              gridCols={4} 
              gridRows={4} 
              binderColor={binderColor}
              onCardClick={handleCardClick}
            />

            {/* Empty state if no cards */}
            {storeCards.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg mb-2">La carpeta está vacía</p>
                <p className="text-gray-500 text-sm">Próximamente agregaremos cartas disponibles</p>
              </div>
            )}
          </div>
        ) : (
          /* Regular Products View */
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filters Sidebar */}
            <aside className={`lg:w-72 flex-shrink-0 ${showFilters ? '' : 'hidden lg:block'}`}>
              <div className="glass-card rounded-xl p-4 md:p-6 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-yellow-400">Filtros</h3>
                  <button 
                    onClick={clearFilters}
                    className="text-xs text-pink-400 hover:text-pink-300"
                  >
                    Limpiar todo
                  </button>
                </div>

                {/* Pay with NirCoin Toggle */}
                {isLoggedIn && (
                  <div className="mb-6 p-3 bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-yellow-600/30 rounded-lg">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={payWithNirCoin}
                        onChange={(e) => setPayWithNirCoin(e.target.checked)}
                        className="w-5 h-5 rounded accent-yellow-400"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <NirCoinIcon size={16} />
                          <span className="text-sm font-semibold text-yellow-400">Pagar con NirCoins</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Saldo: {nirtcgBalance?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Category Filter */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-3">Categoría</h4>
                  <div className="space-y-2">
                    {categories.map(cat => (
                      <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          filters.category.includes(cat.id) 
                            ? 'bg-pink-500 border-pink-500' 
                            : 'border-gray-600 group-hover:border-pink-400'
                        }`}>
                          {filters.category.includes(cat.id) && (
                            <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <input 
                          type="checkbox"
                          checked={filters.category.includes(cat.id)}
                          onChange={() => handleCategoryToggle(cat.id)}
                          className="hidden"
                        />
                        <span className="text-sm text-gray-300 group-hover:text-white">{cat.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range Filter */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-3">Rango de Precio</h4>
                  <div className="px-2">
                    <input
                      type="range"
                      min="0"
                      max="500"
                      value={filters.priceMax}
                      onChange={(e) => setFilters(prev => ({ ...prev, priceMax: parseInt(e.target.value) }))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
                    />
                    <div className="flex justify-between mt-2 text-xs text-gray-400">
                      <span>$0</span>
                      <span className="text-pink-400 font-semibold">${filters.priceMax}</span>
                    </div>
                  </div>
                </div>

                {/* Availability Filter */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-3">Disponibilidad</h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        filters.availability.includes('inStock') 
                          ? 'bg-pink-500 border-pink-500' 
                          : 'border-gray-600 group-hover:border-pink-400'
                      }`}>
                        {filters.availability.includes('inStock') && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <input 
                        type="checkbox"
                        checked={filters.availability.includes('inStock')}
                        onChange={() => handleAvailabilityToggle('inStock')}
                        className="hidden"
                      />
                      <span className="text-sm text-gray-300 group-hover:text-white">En Stock</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        filters.availability.includes('preorder') 
                          ? 'bg-pink-500 border-pink-500' 
                          : 'border-gray-600 group-hover:border-pink-400'
                      }`}>
                        {filters.availability.includes('preorder') && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <input 
                        type="checkbox"
                        checked={filters.availability.includes('preorder')}
                        onChange={() => handleAvailabilityToggle('preorder')}
                        className="hidden"
                      />
                      <span className="text-sm text-gray-300 group-hover:text-white">Preventa</span>
                    </label>
                  </div>
                </div>

                {/* Apply Filters Button (Mobile) */}
                <button 
                  onClick={() => setShowFilters(false)}
                  className="lg:hidden w-full py-2.5 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg font-semibold text-white text-sm"
                >
                  Aplicar Filtros
                </button>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Mobile Filter Toggle */}
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden w-full mb-4 py-2.5 border border-purple-700 rounded-lg text-gray-300 flex items-center justify-center gap-2"
              >
                <ChevronDown className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              </button>

              {/* Results Count */}
              <div className="mb-4 text-sm text-gray-400">
                {filteredProducts.length} productos encontrados en <span className="text-yellow-400">{sections.find(s => s.id === selectedSection)?.name}</span>
              </div>

              {loading ? (
                <div className="text-center py-16">
                  <div className="inline-block w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-gray-400 mt-4">Cargando productos...</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                  {filteredProducts.map(product => (
                    <div key={product.id} className="glass-card rounded-xl overflow-hidden hover:scale-[1.02] transition-transform" data-testid={`product-${product.id}`}>
                      <div className="aspect-square bg-purple-900/50 relative overflow-hidden">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                        {!product.in_stock && !product.is_preorder && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="text-red-400 font-bold text-sm">Agotado</span>
                          </div>
                        )}
                        {product.is_preorder && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-500 rounded text-black text-xs font-bold">
                            Preventa
                          </div>
                        )}
                        {selectedSection === 'store' && (
                          <div className="absolute top-2 right-2 px-2 py-1 bg-purple-600 rounded text-white text-xs font-bold">
                            Tienda
                          </div>
                        )}
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="font-semibold text-white text-xs md:text-sm mb-1 md:mb-2 line-clamp-2">{product.name}</h3>
                        <div className="flex items-center justify-between">
                          <div>
                            {payWithNirCoin && product.price_nircoin ? (
                              <div className="flex items-center gap-1">
                                <NirCoinIcon size={16} />
                                <span className="text-lg md:text-xl font-black text-yellow-400">{product.price_nircoin}</span>
                              </div>
                            ) : (
                              <span className="text-lg md:text-xl font-black text-pink-400">${product.price}</span>
                            )}
                            {product.price_nircoin && !payWithNirCoin && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <NirCoinIcon size={12} />
                                <span>{product.price_nircoin}</span>
                              </div>
                            )}
                          </div>
                          <button 
                            onClick={() => handleAddToCart(product)}
                            disabled={!product.in_stock && !product.is_preorder}
                            className="p-1.5 md:p-2 bg-yellow-400 rounded-full text-black hover:bg-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid={`add-cart-${product.id}`}
                          >
                            <ShoppingCart size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {!loading && filteredProducts.length === 0 && (
                <div className="text-center py-12 md:py-16">
                  <p className="text-gray-400 text-sm md:text-lg mb-4">No se encontraron productos con los filtros actuales</p>
                  <button 
                    onClick={clearFilters}
                    className="px-4 py-2 border border-pink-500 text-pink-400 rounded-lg hover:bg-pink-500/10 text-sm"
                  >
                    Limpiar Filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal 
          card={selectedCard} 
          onClose={() => setSelectedCard(null)} 
          onAddToCart={handleAddToCart}
          payWithNirCoin={payWithNirCoin}
        />
      )}
    </div>
  );
};

export default MarketplacePage;
